import React, { useState, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { lowerCase, isString, isEmpty, isFunction, capitalize, isNaN } from 'lodash-es';
import { Button, Popover, Tag, Tooltip, Typography, Progress, Space, Menu, Checkbox } from 'antd';
import ColumnFilter from 'components/ColumnFilter';
import ColumnFilterSearch from 'components/ColumnFilterSearch';
import { SEVERITY_COLOR, PROGRESS_COLOR, COLORS } from 'constants/Layout';
import moment from 'moment';
import dayjs from 'dayjs';
import ColumnFilterComparison from 'components/ColumnFilterComparison';
import classNames from 'classnames';
import ColumnFilterDateRange from 'components/ColumnFilterDateRange';
import NameAvatar from 'components/NameAvatar';
import { MenuOutlined } from '@ant-design/icons';
import { VOCABULARY_STATUS, GLOBAL_STATUS } from 'views/Findings/constants/status';
import { SEVERITY_NAMES } from 'views/Findings/constants/severity';
import Vocabulary, { getVocabulary, getInnerText } from 'components/Vocabulary';
import ContentOverlay from 'components/ContentOverlay';
import { marked } from 'marked';
import ColumnFilterDueDate from 'components/ColumnFilterDueDate';
import { getStartAndEndDateForQuickOption } from './dateUtils';
import { transformComment } from 'utils/commentUtil';
import FilterableCell from 'components/FilterableCell';
import Sorters from './sorters';
import { DndContext, DragOverlay, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import parseHtml from './parserUtil';

const {Text} = Typography;

export {Sorters};

function GenericMoreItemsRenderer(values = [], className = '', Component, delimiter = ','){
    if(_.isEmpty(values)){
        return 'N/A';
    }
    if(typeof values === 'string'){
        values = [values];
    }
    const resources = values.slice();
    const minResources = resources.splice(0, 2);
    let content = null;
    if(resources.length){
        content = (
            <div style={{maxHeight: 150, maxWidth: 200, overflow: 'auto'}}>
                {
                    values.map((r, i) => [i !== 0 ? delimiter : '', <Component style={{marginRight: 2, marginBottom: 2}} className={className} key={i}>{r.name || r}</Component>])
                }
            </div>
        )
    }
    return (
        <div /* direction="vertical" size={2} */>
            {
                minResources.map((r, i) => [i !== 0 ? delimiter : '', <Component index={i} title={r.name || r} className= {classNames('text-ellipsis tools', className)} style={{marginRight: 2, marginBottom: -2, maxWidth: '100%', display: 'inline-block',fontSize: 13}} key={i}>{r.name || r}</Component>])
            }
            {
                resources.length
                ?
                <Popover content={content}>
                    <Button size="small" type="link" style={{fontSize: 12}}>+{resources.length}</Button>
                </Popover>
                :
                null
            }
        </div>
    )
}

function PlusMoreSingleLineRenderer({ values, limit = 1, className = undefined, Component = 'span', delimiter=', ', style = {}, getExtraComponentProps = undefined}){
    if(_.isEmpty(values)){
        return CellRenderers.VALUE_OR_NA();
    }
    if(_.isString(values)){
        values = [values];
    }
    const resources = _.compact(values.slice());
    const minResources = resources.splice(0, limit);
    let content = null;
    if(resources.length){
        content = (
            <div style={{maxHeight: 150, maxWidth: 200, overflow: 'auto'}}>
                {
                    resources.map((r, i) => [i !== 0 ? delimiter : '', <Component style={{marginRight: 2, marginBottom: 2}} className={className} key={i} {...(isFunction(getExtraComponentProps) ? getExtraComponentProps(r) : {})}>{r.name || r}</Component>])
                }
            </div>
        )
    }
    return (
        <div className="d-flex align-items-center">
            <div className="text-ellipsis" title={_.join(minResources, delimiter)}>
                {
                    minResources.map((r, i) => [i !== 0 ? delimiter : '', <Component index={i} className= {classNames(className)} style={{...{marginRight: 2, marginBottom: -2, maxWidth: '100%', fontSize: 13}, ...style}} key={i} {...(isFunction(getExtraComponentProps) ? getExtraComponentProps(r) : {})}>{r.name || r}</Component>])
                }
            </div>
            {
                resources.length
                ?
                <Popover content={content}>
                    <Button size="small" type="link" style={{fontSize: 12}}>+{resources.length}</Button>
                </Popover>
                :
                null
            }
        </div>
    )
}

function findingTags({ values, limit = 1, className, maxWidth = 200, filterKey, getFilterValue,findingId, showFilter=true }) {
    if (_.isEmpty(values)) {
        return CellRenderers.VALUE_OR_NA();
    }
    if (typeof values === 'string') {
        values = [values];
    }
    const resorces = values.slice();
    const minResources = resorces.splice(0, limit);
    let content = null;

    const tagContent = (text) => {
        return (
            <FilterableCell showFilter={showFilter} className="d-flex align-items-center" key={text} filterKey={filterKey} value={text} getFilterValue={getFilterValue}>
                {filterKey === 'cve' ? <a className="text-ellipsis" rel="noopener noreferrer" target="_blank"  href={`/compliance/vulnerability-rules/${text}/redirect?findingId=${findingId}`}>{text}</a> : <span className="text-ellipsis">{text}</span>}
            </FilterableCell>
        )
    };
    
    if (resorces.length) {
        content = (
            <div style={{ maxHeight: 150, maxWidth: 200, overflow: 'auto' }}>
                {
                    _.map(values, (text, i) => <>{i !==0 ? ',' : ''} {_.isEmpty(text) ? CellRenderers.VALUE_OR_NA() :
                    <Tag style={{ marginRight: 2, marginBottom: 2 }} className={classNames(className)} key={`${text}-${i}`}>
                        {tagContent(text)}
                    </Tag>}</>)
                }
            </div>
        )
    }
    return (
        <div style={{verticalAlign: 'middle'}}>
            {
                _.map(minResources, (text, i) => _.isEmpty(text) ? CellRenderers.VALUE_OR_NA() :
                    <Tag title={text} className={classNames('text-ellipsis tools', className)} style={{ marginRight: 2, verticalAlign: 'inherit', maxWidth, fontSize: 13 }}>
                        {tagContent(text)}
                    </Tag>
                )
            }
            {
                resorces.length
                    ?
                    <Popover content={content}>
                        <Typography.Link className="p-l-0" style={{ fontSize: 12, verticalAlign: 'middle' }}>+{resorces.length}</Typography.Link>
                    </Popover>
                    :
                    null
            }
        </div>
    )
}

function SpanComponent(props){
    return <span {...props} style={props.index === 0 ? { ...props.style } : { ...props.style, marginLeft: 5 }} />
}

function MoreItemsRenderer(values, className){
    return GenericMoreItemsRenderer(values, className, SpanComponent);
}

function TagsRenderer(values, className){
    return GenericMoreItemsRenderer(values, className, Tag);
}

function CustomizedSeverityRenderer(severity = '', className = ''){
    if(_.isEmpty(severity)){
        return '';
    }
    if(!SEVERITY_NAMES[severity.toLowerCase()]){
        return severity;
    }
    return <Tag style={{ maxWidth: '100%', paddingInline: 8 }} className={`${severity.toLowerCase()} severity-width text-ellipsis ${className}`} title={getVocabulary(severity)}>{getVocabulary(severity)}</Tag>
}

function SeverityRenderer(severity = '', className = '') {
        if(_.isEmpty(severity)){
            return '';
        }
        if(!SEVERITY_NAMES[severity.toLowerCase()]){
            return severity;
        }
        return <Tag className={`${severity.toLowerCase()} severity-width ${className}`}>{startCaseWithSpace(severity)}</Tag>
}


const getTagColor = (status) =>{
    let color;
    switch(status){
        case 'ACTIVE':
            color = 'activeStatus'
            break;
        case 'INACTIVE':
            color = 'info'
            break;
        case 'PARTIAL':
            color = 'partial'
            break;
        case 'NOT_CONFIGURED':
            color = 'medium'
            break;
        case 'EXCLUDED':
            color = 'excluded'
            break;
        default :
           color = 'critical'
    }
    return color;
}

function operationalStatusRenderer(status = '', record = {}) {
    const { operationalStatusReason , operationalStatusMessage} = record;
    if(_.isEmpty(status)){
        return '';
    }
    return <Tooltip title={operationalStatusReason || operationalStatusMessage}>
                <Tag className={`${getTagColor(status)} ${status} break-content`}>{status === 'CONFIG_ERROR' ? startCaseWithSpace('Error') : startCaseWithSpace(status)}</Tag>
           </Tooltip>;
}

function scanStatusRenderer(status = ''){
    if(_.isEmpty(status)){
        return 'N/A';
    }
    let className;
    switch(status)
    {
        case 'PASSED':
            className='activeStatus'
            break;
        case 'FAILED':
            className='critical'
            break;
        case 'RUNNING':
            className='running'
            break;
        case 'PUBLISHED':
            className = 'medium'
            status = 'INQUEUE' 
            break;
        default:
            className='medium'   

    }
    return (
        status ==='INQUEUE' ? (
            <Tooltip title="Report request has been received by the ArmorCode job scheduler. It will be processed shortly.">
                <Tag className={`${className} severity-width`}>{startCaseWithSpace(status)}</Tag>
            </Tooltip>
        ) : (
            <Tag className={`${className} severity-width`}>{startCaseWithSpace(status)}</Tag>
        )
    );
}

function runbookStatusRenderer(status = ''){
    if(_.isEmpty(status)){
        return 'N/A';
    }
    let className = 'red';
    switch(status)
    {
        case 'COMPLETED':
            className='activeStatus'
            break;
        case 'FAILED':
            className='critical'
            break;
        case 'STOPPED':
        case 'CANCELLED':
            className='high'
            break;
        case 'STARTED':
            className='running'
            break;
        case 'CREATED':
            className='info'
            break;
        default:
            className='info'   

    }
    return <Tag className={className} style={{ width: '75px' }} >{startCaseWithSpace(status)}</Tag>
}

function scanTooltip(displayLetter,count,finding,record,onClick=_.noop,findingName){
    let _onClick;
    const disableClick = displayLetter === 'T' || displayLetter === 'D';
    if(count && record.scanStatus === 'COMPLETED' && !disableClick) {
        _onClick = () => onClick(record.id, findingName, record)
    }
    return (<Tooltip color={'white'} title={(<span className="" style={{backgroundColor:'white'}}>
    {(<Text className="critical">{`${getVocabulary('critical')} : ${finding?.severity?.Critical ?? 0}`}</Text>) || 0}<br/>
    {<Text className="high">{`${getVocabulary('high')} : ${finding?.severity?.High ?? 0}`}</Text>}<br/>
    {<Text className="medium">{`${getVocabulary('medium')} : ${finding?.severity?.Medium ?? 0}`}</Text>}<br/>
    {<Text className="low-text">{`${getVocabulary('low')} : ${finding?.severity?.Low ?? 0}`}</Text>}<br/>
    {<Text className="info">{`${getVocabulary('info')} : ${finding?.severity?.Info ?? 0}`}</Text>}
    </span>)} > 
    <Tag className={classNames('scan-finding',{ clickable: !!_onClick })} onClick={_onClick}> {displayLetter} <Text className="finding-text">{(count) ? formatNumber({count: +count, showTooltip: false}) : '-'}</Text></Tag>
    </Tooltip>)
}

function TotalFindingRenderer(finding = {},record,onClick){
    return (<>
    {scanTooltip('T',(finding.totalFindings && finding.totalFindings.count) || 0,finding.totalFindings, record, onClick, 'total')}
    {scanTooltip('D',(finding.duplicateFindings && finding.duplicateFindings.count) || 0,finding.duplicateFindings, record, onClick, 'duplicate')}
    {scanTooltip('R',(finding.resolvedFindings && finding.resolvedFindings.count) || 0, finding.resolvedFindings, record, onClick, 'resolved')}
    {scanTooltip('N',(finding.newFindings && finding.newFindings.count) || 0, finding.newFindings, record, onClick, 'new')}
    </>)
}

export function SubProductRenderer({name, productName}) {
    return (
        <div className="d-flex flex-column align-items-flex-start">
            <Text className="m-b-xs">{name}</Text>
            <Tag>{productName}</Tag>
        </div>
    );
}

export function TotalFindings(severity = {}, showMedium = true, showLow = true, hideNull = false) {
    return (<>
        <SeverityTagRenderer severity="critical" count={severity.Critical} hideNull={hideNull} />
        <SeverityTagRenderer severity="high" count={severity.High} hideNull={hideNull} />
        {showMedium && <SeverityTagRenderer severity="medium" count={severity.Medium} hideNull={hideNull} />}
        {showLow && <SeverityTagRenderer className="m-r-0" severity="low" count={severity.Low} hideNull={hideNull} />}
    </>)
}

function SeverityTagRenderer({severity = '', className = '', count, hideNull}) {
    return hideNull && _.isNull(count) ? null : <Tag className={`${lowerCase(severity) } severity-tag ${className}`}><Text className="finding-text">{(count) || 0}</Text>&nbsp;{getVocabulary(severity)[0]}&nbsp;</Tag>
}

export const renderTrend = ({
    severityKey, 
    oldSeverity = {}, 
    severity, 
    totalCount, 
    newTrendDataFormat = false, 
    hideTrend = false, 
    showTrendTooltips = false, 
    trendDayDiff = 30
}) => {
    let trendValue;
    let content;
    if (hideTrend) return null;
    if (newTrendDataFormat) {
        trendValue = severity[`${severityKey.toLowerCase()}Trend`] || 0;
    } else {
        if (_.isUndefined(oldSeverity)) {
            return null;
        }
        let newVal;
        let oldVal;
        if (severityKey !== 'total') {
            newVal = severity[capitalize(severityKey)] || 0;
            oldVal = oldSeverity[capitalize(severityKey)] || 0;
        } else {
            newVal = totalCount(severity);
            oldVal = totalCount(oldSeverity);
        }
        trendValue = newVal - oldVal;
    }

    if(showTrendTooltips){
        trendValue = getTrendTooltipCount(severityKey, severity);
    }

    if(trendValue === 0){
        content = 'No change';
     }
     else if(trendValue > 0){
        content = `+${trendValue} in last ${trendDayDiff} days`;
     }
     else{
        content = `${trendValue} in last ${trendDayDiff} days`;
     }

    return <span className="m-r-sm">{content}</span>;
};

export function formatNumber({count, onlyString = false, showTooltip = true, fontSize = 10 }) {
    if(!_.isNumber(count)) return count;

    let formattedNumber = count.toString();

    const ranges = [{
        divider: 1E3,
        suffix: 'K'
    }, {
        divider: 1E6,
        suffix: 'M'
    }, {
        divider: 1E9,
        suffix: 'B'
    }];

    for (let index = ranges.length - 1; index >= 0; index--) {
        if (count > ranges[index].divider) {
            let quotient = count / ranges[index].divider;

            if (quotient < 10) {
                quotient = Math.floor(quotient * 10) / 10;
            } else {
                quotient = Math.floor(quotient);
            }

            formattedNumber = quotient.toString() + ranges[index].suffix;
            break;
        }
    }
    const formattedString = onlyString ? formattedNumber : <span style={{fontSize}}>{formattedNumber}</span>
    return showTooltip ? <Tooltip title={formatNumberWithCommas(count)}>{formattedString}</Tooltip> : formattedString;  
  }

export function formatNumberWithCommas(number) {
    const _number = Number(number);
    return _.isNaN(_number) ? number : _number.toLocaleString();
}

function ProgressRenderer(percent){
    if(percent === -1){
        return (<Progress type="circle" width={PROGRESS_COLOR['width']} strokeWidth={12} strokeColor={{'0%': PROGRESS_COLOR['error'],'100%': PROGRESS_COLOR['error']}} percent={100} status="exception"/>)    
    }
    return (<Progress type="circle" width={PROGRESS_COLOR['width']} strokeWidth={12} strokeColor={{'0%': PROGRESS_COLOR[0],'100%': PROGRESS_COLOR[100]}} percent={percent}/>)
}

function RelativeTimeRenderer(value){
    if(!value){
        return 'N/A'
    }
    return <RelativeTimeComponent value={value} />
}

function TotalTimeTaken(startTime, endTime){
    if(!startTime || !endTime){
        return 'N/A'
    }
    
    const start = moment(startTime);
    const end = moment(endTime);
    const difference = end.diff(start, 'milliseconds');

    return moment().add(difference, 'milliseconds').fromNow(true)
}

function RelativeTimeComponent({value, suffix = true}) {
    const [state, setState] = useState({ difference: 0, output: '' });

    const renderRelativeTime = useCallback(() => {
        const currentTimestamp = moment();
        const oldTimeStamp = moment(value);

        let type;
        const difference = Math.abs(currentTimestamp.diff(oldTimeStamp, 'seconds'));
        const hours = Math.abs(Math.round(currentTimestamp.diff(oldTimeStamp, 'hours', true)));
        if (hours < 24) { // if time less than 24 hrs calculate in hrs, mins & secs
            const secondsDiff = Math.abs(Math.round(currentTimestamp.diff(oldTimeStamp, 'seconds', true)));
            const minutesDiff = Math.abs(Math.round(currentTimestamp.diff(oldTimeStamp, 'minutes', true)));
            if (secondsDiff < 60) {
                type ='seconds';
            } else if (minutesDiff < 60) {
                type = 'minutes';
            } else {
                type = 'hours';
            }
        } else {
            const daysDiff = Math.abs(Math.round(currentTimestamp.startOf('day').diff(oldTimeStamp.startOf('day'), 'days', true)));
            const monthDiff = Math.abs(Math.round(currentTimestamp.startOf('day').diff(oldTimeStamp.startOf('day'), 'months', true)));
            if (daysDiff < 30) {
                type ='days';
            } else if (monthDiff < 12) {
                type = 'months';
            } else {
                type = 'years';
            }
        }
        let time;
        if (hours < 24) { // if time less than 24 hrs calculate in hrs, mins & secs including time.
            time = currentTimestamp.diff(oldTimeStamp, type, true);
        } else {
            time = currentTimestamp.startOf('day').diff(oldTimeStamp.startOf('day'), type, true);
        }
        let output;
        const absoluteTime = Math.round(time);
        if (Math.abs(absoluteTime) === 1) {
            output = `${Math.abs(absoluteTime)} ${type.slice(0, -1)}`;
        } else if (time === 0 || type === 'seconds') {
            output = `a few ${type}`;
        } else {
            output = `${Math.abs(absoluteTime)} ${type}`;
        }
        if (absoluteTime >= 0) {
            output += suffix ? ' ago' : '';
        } else {
            output = `in ${output}`;
        }
        setState({ type, difference, output, time });
    }, [value, suffix])

    useEffect(() => {
        renderRelativeTime();
    }, [value, renderRelativeTime]);

    const getDelayTime = useCallback(() => {
        const { type, time } = state;
        let delay = 0;
        switch(type) {
            case 'seconds':
            case 'minutes':
                delay = 60;
                break;
            case 'hours':
                if (time + 1 === 0) {
                    delay = 60;
                } else {
                    delay = (Math.abs(time - parseInt(time)) || 1) * 60 * 60;
                }
                break
            default: break;
        }
        return delay * 1000;  // convert to milliseconds;
    }, [state])

    useEffect(() => {
        const { type } = state;
        if (['seconds', 'minutes', 'hours'].includes(type)) {
            const timer = setTimeout(() => {
                renderRelativeTime();
              }, getDelayTime());
            return () => clearTimeout(timer);
        }
    }, [renderRelativeTime, state, getDelayTime]);


    return (
        <Tooltip title={moment(value).format('MM/DD/YYYY hh:mm A')}>
            {state.output}
        </Tooltip>
    );
}

function TimeRenderer(value){
    if(!value){
        return 'N/A'
    }

    const parsedTime = dayjs.isDayjs(value) ? value : dayjs(value);
    if (!parsedTime.isValid()) {
        return 'N/A';
    }

    const machineReadable = parsedTime.format('YYYY-MM-DDTHH:mm:ssZ');
    const humanReadable = parsedTime.format('MM/DD/YYYY hh:mm A');
    
    return (
        <time
            dateTime={machineReadable}>
            {humanReadable}
        </time>
    );
}

function DateRenderer(value) {
  if (!value) {
    return 'N/A';
  }
  if (_.isString(value)) {
    const numValue = +value;
    if (!isNaN(numValue)) {
      value = numValue;
    }
  }

  if (moment(value).isValid()) {
    return moment(value).format('MM/DD/YYYY');
  }
  return value;
}

function NameIdMapRenderer(object = {}){
    const name = _.get(object, 'name', object);
    if(!name || typeof name !== 'string'){
        return <div>N/A</div>;
    }
    return name;
}

function StatusDotRenderer(status){
    // const dotStyle = { height: 20, width: 20, backgroundColor: '#BFBFBF', borderRadius: '50%' };
    return (
        <div 
            style={{
                width: 15, 
                height: 15, 
                backgroundColor: (status === 'success' ? COLORS.success : status === 'default' ? COLORS.default : SEVERITY_COLOR.critical),
                borderRadius: '50%'
            }}
        />
    )
}

function AgeRenderer(time){
    if(!time){
        return CellRenderers.VALUE_OR_NA();
    }
    return <RelativeTimeComponent value={time} suffix={false} />
}

function VocabularyRenderer(value){
    const name = _.isObject(value) ? value.name : value;
    if(name){
        const vocabName = VOCABULARY_STATUS.includes(GLOBAL_STATUS[name]) ? GLOBAL_STATUS[name] : name.toLowerCase();
        return getVocabulary(vocabName)
    }
    return 'N/A'
    
}

export default function EllipsisCell(text){
	return (
        <div className="ellipsis-cell" style={{width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} >
            <Tooltip title={text}>
                {text}
            </Tooltip>
		</div>
	)
}

const nameWithInitialCircle = (name) => <div className="d-flex align-items-center"><NameAvatar name={name} /><div className="m-l-xs">{name}</div></div>;

function RenderHtmlEditor(value) {
    return <div className="message">{parseHtml(marked.parse(CellRenderers.VALUE_OR_NA(value)), { transform: transformComment })}</div>
}

function renderSingleLineTags(tags){
    if (isEmpty(tags)) {
        return CellRenderers.VALUE_OR_NA();
    }
    return (
        <div className="d-flex">
            <div
                className="text-ellipsis"
                style={{ marginRight: 4 }}
                title={tags[0]}
            >
                {tags[0]}
            </div>
            {tags?.length > 1 ? (
                <Popover
                    content={
                        <ul className="m-b-0 p-l-m">
                            {tags.slice(1, tags.length).map((t) => (
                                <li key={t}>{t}</li>
                            ))}
                        </ul>
                    }
                >
                    <Tag>+{tags.length - 1}</Tag>
                </Popover>
            ) : null}
        </div>
    );
  }
export const CellRenderers = {
    TAGS: TagsRenderer,
    PLUS_MORE: MoreItemsRenderer,
    PLUS_MORE_SINGLE_LINE: PlusMoreSingleLineRenderer,
    TAGS_AS_FILTER: findingTags,
    SEVERITY: SeverityRenderer,
    CUSTOMIZED_SEVERITY: CustomizedSeverityRenderer,
    AllFinding:TotalFindingRenderer,
    SEVERITY_TAGS: TotalFindings,
    SUB_PRODUCT: SubProductRenderer,
    Progress:ProgressRenderer,
    TIME_RELATIVE: RelativeTimeRenderer,
    TIME: TimeRenderer,
    DATE: DateRenderer,
    NAME_ID_MAP: NameIdMapRenderer,
    NAME_ID_MAP_LIST: (list = []) => {
        if(_.isEmpty(list)){
            return CellRenderers.VALUE_OR_NA(list);
        }
        return list.map((item, i) => <NameIdMapRenderer {...item} key={i} />)
    },
    STATUS_DOT: StatusDotRenderer,
    VALUE_OR_NA: (value) => (_.isEmpty(value) && !_.isNumber(value)) ||  value==='' || (['Not Available', 'n/a'].includes(value)) ? 'N/A' : value ,
    VALUE_OR_BLANK: (value) => (_.isEmpty(value) && !_.isNumber(value)) ||  value==='' || (['Not Available', 'n/a'].includes(value)) ? '<Blank>' : value ,
    VALUE_OR_ZERO: (value) => (_.isEmpty(value) && !_.isNumber(value)) ? '0' : value,
    ELLIPSIS: EllipsisCell ,
    OPERATIONAL_STATUS: operationalStatusRenderer,
    SCAN_STATUS: scanStatusRenderer,
    RUNBOOK_STATUS: runbookStatusRenderer,
    AGE: AgeRenderer,
    NAME_WITH_INITIALS_CIRCLE: nameWithInitialCircle,
    VOCABULARY_RENDERER: VocabularyRenderer,
    TOTAL_TIME_TAKEN: TotalTimeTaken,
    HTML_EDITOR: RenderHtmlEditor,
    TAGS_SINGLE_LINE: renderSingleLineTags,
}

export const columnRenderArrayData = (data) =>{
    if(_.isEmpty(data)) {
        return CellRenderers.VALUE_OR_NA(data)
    }
    const renderData = data.split(',');
    const renderName = renderData.map((item, i) => <p key={i} className={i === renderData.length - 1 ? 'm-b-0' : 'm-b-xs'}>{item}</p>)
    return <ContentOverlay content={renderName} minHeight={40}/>
}

export function onColumnFilter(dataIndex, {nestedValuePath, onlySearch, exactMatch, nullFilter, isUIDateRangeFilter}){
    return (filterValue = '', record) => {
        let value = _.result(record, dataIndex);
        if(filterValue === nullFilter && !_.isUndefined(nullFilter)) return _.isUndefined(value) || _.isNull(value) || (_.isArray(value) && _.isEmpty(value));
        if((value === undefined || value === null)){
            return false;
        }
        if(typeof value === 'string'){
            if(onlySearch){
                return value.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1;
            }
            if(exactMatch){
                return value === filterValue;
            }
            return value.toLowerCase() === filterValue.toLowerCase();
        }
        if(typeof value === 'number'){
            if(isUIDateRangeFilter === true){
                let dateRange = filterValue.split('-');
                if(dateRange.length === 1){
                    dateRange = getStartAndEndDateForQuickOption({
                      label: dateRange[0],
                    }).map(m => m.toDate().getTime());
                }
                return (value >= Number(dateRange[0]) && value <= Number(dateRange[1]));
            }
            if(onlySearch){
                return value.toString().indexOf(filterValue.toString()) !== -1;
            }
            return value === filterValue;
        }
        if(typeof value === 'boolean') {
            return value === filterValue;
        }
        if(_.isArray(value) && _.isPlainObject(value[0])){
            value = value.map(v => _.result(v, nestedValuePath));
        } else if(_.isPlainObject(value)){
            value = _.result(value, nestedValuePath);
        }
        return exactMatch ? value === filterValue : value.indexOf(filterValue) !== -1;
    }
}

export function getFilterProps({
    dataIndex,
    dataSource,
    filters = undefined,
    nestedValuePath = 'name',
    onlySearch = false,
    noFilterFunction = false,
    filterLabelFormatter = undefined,
    exactMatch = undefined,
    sortType = undefined,
    sortOptions = undefined,
    filterCompareLabel = undefined,
    applyInterceptor = _.identity,
    receiveInterceptor = _.identity,
    onUpdateValues = undefined,
    supportFilterOperation = false,
    ...rest
}){
    // To display name in filters instead of email, we leverage "getNameList" method from GlobalUserListContext
    if (_.isFunction(rest.getNameList) && filters) {
        filters = rest.getNameList(filters);
        filterLabelFormatter = (name, value) => name
    }

    const filterType = onlySearch ? ColumnFilterTypes.SEARCH : rest.filterType;
    if(_.isEmpty(filters) && !_.isEmpty(dataSource)){
        const _filters = [];
        const mapper = (o) => {
            let value = _.result(o, dataIndex);
            if(_.isArray(value) && _.isPlainObject(value[0])){
                value = value.map(v => _.result(v, nestedValuePath));
            } else if (_.isPlainObject(value)){
                value = _.get(value, nestedValuePath);
            }
            return value;
        }
        filters = _.uniq(_.compact(_.flatten(dataSource.map(mapper)))).map(key => ({value: key, text: key}));
        if(sortType === 'SEVERITY'){
            Object.keys(SEVERITY_COLOR).map(_.toUpper).filter(key => _.filter(filters, (o) => { if(_.includes(o.value, key)) _filters.push(o) }));
            filters = [..._filters]
        }
    }

    return {
        filters,
        filterLabelFormatter,
        isUIDateRangeFilter: rest.isUIDateRangeFilter,
        filterDropdown: (props) => {
            if(filterType === ColumnFilterTypes.SEARCH){
                return <ColumnFilterSearch supportFilterOperation={supportFilterOperation} {...props} nullFilterValue={rest.nullFilterValue} isMultipleValueSupported={rest.isMultipleValueSupported} />
            } else if (filterType === ColumnFilterTypes.COMPARISON){
                return <ColumnFilterComparison supportFilterOperation={supportFilterOperation} {...props} onUpdateValues={onUpdateValues} showHelpText={rest.showHelpText} filterLabel={filterCompareLabel}  nullFilterValue={rest.nullFilterValue} applyInterceptor={applyInterceptor} receiveInterceptor={receiveInterceptor} />
            } else if (filterType === ColumnFilterTypes.DATE_RANGE){
                return <ColumnFilterDateRange supportFilterOperation={supportFilterOperation} {...props} key={'column-date-range' + props.visible} isUIDateRangeFilter={rest.isUIDateRangeFilter} allowFutureDates={rest.allowFutureDates} />
            } else if (filterType === ColumnFilterTypes.DUE_DATE){
                return <ColumnFilterDueDate supportFilterOperation={supportFilterOperation} {...props} />
            }
            return <ColumnFilter supportFilterOperation={supportFilterOperation} {...props} sortOptions={sortOptions} filterLabelFormatter={filterLabelFormatter} filters={filters} nullFilterValue={rest.nullFilterValue} nullFilterLabel={rest.nullFilterLabel} />;
        },
        onFilter: !noFilterFunction && onColumnFilter(dataIndex, {nestedValuePath, onlySearch: filterType === ColumnFilterTypes.SEARCH, exactMatch, nullFilter: rest.nullFilterValue, isUIDateRangeFilter: rest.isUIDateRangeFilter}),
        filterCompareLabel,
        applyInterceptor,
        receiveInterceptor,
        nullFilterValue: rest.nullFilterValue,
        nullFilterLabel: rest.nullFilterLabel
    }
}

export const ColumnFilterTypes = {
    SEARCH: 'SEARCH',
    COMPARISON: 'COMPARISON',
    DEFAULT: 'DEFAULT',
    DATE_RANGE: 'DATE_RANGE',
    DUE_DATE: 'DUE_DATE'
}

export const OWNER_TITLE_MAP = {
    devOwner: 'Dev Owner',
    projectOwner: 'Project Owner',
    businessOwner: <Vocabulary name="businessOwner" />,
    engineeringOwner: <Vocabulary name="engineeringOwner" />,
    securityOwner: <Vocabulary name="securityOwner" />,
    supportOwner: <Vocabulary name="supportOwner" />,
    complianceOwner: <Vocabulary name="complianceOwner" />
}

export function OwnerCell(ownerName = 'businessOwner', options = {}){
    let fieldPath = ownerName;
    if (options.nestedFieldPath) {
        fieldPath = `${options.nestedFieldPath}.${ownerName}`;
    }
    const key = `${fieldPath}.${options.displayString || 'displayName'}`;
    return {
        title: OWNER_TITLE_MAP[ownerName],
        dataIndex: key.split('.'),
        key,
        sorter: Sorters.STRING(key),
        optional: options.optional,
        default: options.default,
        ellipsis:options.ellipsis,
        render: (value, record) => {
           return !_.isEmpty(record[`${ownerName}Name`]) ? record[`${ownerName}Name`] : (value || CellRenderers.VALUE_OR_NA())
        },
        ...(
            (options.dataSource || options.filters) ? getFilterProps({dataIndex: key, dataSource: options.dataSource, filters: options.filters, nullFilterValue: null}) : {}
        )
    }
}
export function OwnerCellV2(ownerName = 'businessOwner', options = {}){
    const key = `${ownerName}Name`;
    return {
        title: OWNER_TITLE_MAP[ownerName],
        dataIndex: key,
        key,
        sorter: Sorters.STRING(key),
        optional: options.optional,
        default: options.default,
        ellipsis:options.ellipsis,
        render: value => value || CellRenderers.VALUE_OR_NA(),
        ...(
            (options.dataSource || options.filters) ? getFilterProps({dataIndex: key, dataSource: options.dataSource, filters: options.filters, nullFilterValue: null}) : {}
        ),
        width: options.width
    }
}

export const Cells = {
    OWNER: OwnerCell,
    OWNER_V2: OwnerCellV2
}

export const DEFAULT_PAGINATION = { page: 0, size: 10 };

export function startCaseWithSpace(text){
    return _.startCase(text.replace(/_/g, ' ').toLowerCase());
}

export function getDataIndex(column){
    return column?.key || (_.isArray(column?.dataIndex) ? column?.dataIndex.join('.') : column?.dataIndex);
}

export function getFilterTitle(column){
    return column?.filterTitle || column?.title
}

export const RowSpace = {
    body: {
        row: (restProps) => (
           <>
                <tr className="spacing" />
                <tr {...restProps} />
                <tr className="spacing" />
            </>
        )
    }
}

export const RowSeverity = (record) => ({className:  `${_.lowerCase(record.severity)}-row`})

export const MakeDNDMenu = ({
  selectedColumnOrder,
  notSelectedColumnOrder,
  activeOptionalColumns,
  onDragEnd,
  updateColumns,
  disableDND,
}) => {
  const sortedNotSelectedColumn = notSelectedColumnOrder.sort(
    Sorters.STRING('text'),
  );
  const [activeId, setActiveId] = useState(null);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedColumnOrder.findIndex(
        (c) => c.value === active.id,
      );
      const newIndex = selectedColumnOrder.findIndex(
        (c) => c.value === over.id,
      );
      const itemIndex = {
        source: { index: oldIndex },
        destination: { index: newIndex },
      };
      onDragEnd(itemIndex);
    }
    setActiveId(null);
  }, [onDragEnd, selectedColumnOrder]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const activeColumn = useMemo(() => selectedColumnOrder.find((c) => c.value === activeId), [activeId, selectedColumnOrder]);

  return (
    <DndContext
      sensors={useSensors(useSensor(PointerSensor))}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext
        items={selectedColumnOrder.map((c) => c.value)}
        strategy={verticalListSortingStrategy}
      >
        <Menu className="p-l-xs p-b-xs" data-drag-disable={disableDND}>
          {selectedColumnOrder.map((c) => {
            return (
              <DNDMenuItems
                c={c}
                key={c.value}
                disableDND={disableDND}
                activeOptionalColumns={activeOptionalColumns}
                updateColumns={updateColumns}
              />
            );
          })}
          <DragOverlay>
            {activeColumn ? (
              <DNDMenuItems
                c={activeColumn}
                key={activeColumn.value}
                disableDND={disableDND}
                activeOptionalColumns={activeOptionalColumns}
                updateColumns={updateColumns}
              />
            ) : null}
          </DragOverlay>

          <div className="m-y-m">Not Selected</div>
          {sortedNotSelectedColumn.map((c) => {
            return (
              <div className="p-y-xs" key={c.value}>
                  <Checkbox
                    data-index={c.value}
                    checked={activeOptionalColumns.indexOf(c.value) !== -1}
                    onChange={updateColumns}
                  >
                    <span title={c.text}>{c.text}</span>
                  </Checkbox>
              </div>
            );
          })}
        </Menu>
      </SortableContext>
    </DndContext>
  );
};

const DNDMenuItems = ({
  c,
  disableDND,
  activeOptionalColumns,
  updateColumns,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: c.value, disabled: disableDND });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="p-y-xs d-flex align-items-center justify-content-between"
    >
      <MenuOutlined
        {...listeners}
        style={{
          cursor: disableDND ? 'not-allowed' : 'grab',
          marginRight: 8,
        }}
      />
      <Checkbox
        className="w-100"
        data-index={c.value}
        checked={activeOptionalColumns.indexOf(c.value) !== -1}
        onChange={updateColumns}
      >
        <span title={c.text}>{c.text}</span>
      </Checkbox>
    </div>
  );
};

export const setInitialDataAndGetOptionalColumns = (userPreferencesContext, tableKey, columns) => {
    const activeOptionalColumns = [];
    let newColumnsIntroduced = [];
    let optionalColumns = [];
    if(tableKey){
        const _columns = userPreferencesContext.getSavedColumnPreferences(tableKey, columns);
        newColumnsIntroduced = userPreferencesContext.setInitialTablePreferences(tableKey, _columns);
        optionalColumns = _columns.filter(c => c.optional).map(c => {
            if(c.default || (newColumnsIntroduced.indexOf(c.dataIndex) !== -1)){
                activeOptionalColumns.push(getDataIndex(c));
            }
            return {text: getInnerText(getFilterTitle(c)), value: getDataIndex(c)};
        });
    }

    return { activeOptionalColumns, optionalColumns };
};

export const MAX_DATA_MSG = 'Sorry, we do not serve more than 10000 results for any findings query.';
export const MAX_DATA_ERROR_MSG = 'Maximum 10000 records can be fetched';

const convertToCSV = (objArray) => {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    for (let i = 0; i < array.length; i++) {
        let line = '';
        const objKeys = Object.keys(array[i]);
        for (let j = 0; j < objKeys.length; j++) {
            const key = objKeys[j];
            line += array[i][key];
            if (j < objKeys.length - 1) {
                line += ','
            }
        }
        str += line + '\r\n';
    }
    return str;
}

export const exportCSVFile = (data, fileTitle='export') => {
    const jsonObject = JSON.stringify(data);
    const csv = isString(data) ? data : convertToCSV(jsonObject);
    const exportedFilename = fileTitle + '.csv';

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, exportedFilename);
    } else {
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', exportedFilename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
}

const getTrendTooltipCount = (severityKey, severity)=>{
    if(!severityKey || _.isEmpty(severity)){
        return;
    }
    let trendValue;
    switch (severityKey) {
        case 'Critical':
            trendValue = severity.prevCritical !== undefined ? severity[severityKey] - severity.prevCritical : undefined;
            break;
        case 'High':
            trendValue = severity.prevHigh !== undefined ? severity[severityKey] - severity.prevHigh : undefined;
            break;
        case 'Medium':
            trendValue = severity.prevMedium !== undefined ? severity[severityKey] - severity.prevMedium : undefined;
            break;
        case 'Low':
            trendValue = severity.prevLow !== undefined ? severity[severityKey] - severity.prevLow : undefined;
            break;
        case 'Total':
            trendValue = severity.prevTotal !== undefined ? severity.total - severity.prevTotal : undefined;
            break;
        default:
            break;    
    }
    return trendValue;
}

export const numberFormatter = (value) => {
    return formatNumber({count: value, onlyString: true , showTooltip: false})
}

export const pageSizeOptions = [10, 20, 50, 100];
